import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { setNotifier } from '../utils/notify';

// Context
const NotifyContext = createContext(null);

// Provider
export function NotifyProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info'); // success, error, warning, info

  const notify = useCallback((msg, options = {}) => {
    setMessage(msg);
    setSeverity(options.variant || options.severity || 'info');
    setOpen(true);
  }, []);

  // Устанавливаем глобальную функцию notify для axios
  useEffect(() => {
    setNotifier(notify);
    return () => setNotifier(null);
  }, [notify]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotifyContext.Provider>
  );
}

// Hook
export function useNotify() {
  const notify = useContext(NotifyContext);
  if (!notify) {
    throw new Error('useNotify must be used within NotifyProvider');
  }
  // Стабильные ссылки на функции — без мемоизации каждый рендер возвращал бы
  // новые success/error/warning/info, что при использовании в зависимостях
  // useCallback/useEffect приводит к бесконечным ререндерам и потоку запросов.
  return useMemo(() => ({
    notify,
    success: (msg) => notify(msg, { severity: 'success' }),
    error: (msg) => notify(msg, { severity: 'error' }),
    warning: (msg) => notify(msg, { severity: 'warning' }),
    info: (msg) => notify(msg, { severity: 'info' }),
  }), [notify]);
}

export default NotifyProvider;
